import {
  getFieldTypeDefinition,
  isFormFieldType,
  type FieldOwnership,
  type FormFieldType,
} from '@dispatch/domain';
import type {
  TemplateCalculation,
  TemplateConditionExpression,
  TemplateDefinition,
  TemplateField,
  TemplateRule,
  TemplateSignatureSignerType,
  TemplateSystemFieldSource,
} from '@dispatch/schemas';
import { Alert, LoadingPanel } from '@dispatch/ui';
import { useCallback, useEffect, useMemo, useState, type ChangeEvent, type DragEvent } from 'react';
import { apiRequest, errorMessage } from './api.js';
import { ConditionEditor } from './ConditionEditor.js';
import {
  addField,
  addNestedField,
  addPage,
  addSection,
  calculationForField,
  cloneDefinition,
  createRule,
  moveFieldToSection,
  moveId,
  nextSymbolicId,
  phase4FieldTypes,
  removeCalculation,
  removeField,
  removePage,
  removeSection,
  reorderIds,
  upsertCalculation,
} from './form-builder-model.js';
import { FormRuntimePreview } from './FormRuntimePreview.js';
import { SortableItem, SortableList } from './SortableList.js';
import type { ReferenceListSummary, TemplateSummary } from './types.js';

interface TemplateValidationIssue {
  code: string;
  message: string;
  path?: string;
}

interface TemplateValidationReport {
  valid: boolean;
  errors: TemplateValidationIssue[];
  warnings: TemplateValidationIssue[];
  checkedAt?: string;
}

interface TemplateVersionRecord {
  id: string;
  template_id: string;
  version_number: number | null;
  status: 'draft' | 'published';
  definition: TemplateDefinition;
  validation_report: TemplateValidationReport;
  definition_sha256: string;
  updated_at: string;
}

const fieldTargetEffects = new Set<TemplateRule['effect']>([
  'show',
  'hide',
  'require',
  'unrequire',
  'set_value',
]);

function editableValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function groupLabel(value: string): string {
  return value
    .split('_')
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

const systemFieldSourceOptions: readonly {
  value: TemplateSystemFieldSource;
  label: string;
}[] = [
  { value: 'job_number', label: 'Job number' },
  { value: 'external_reference', label: 'External reference' },
  { value: 'business', label: 'Business' },
  { value: 'form_name', label: 'Form name' },
  { value: 'form_version', label: 'Form version' },
  { value: 'engineer', label: 'Engineer' },
  { value: 'coordinator', label: 'Coordinator' },
  { value: 'customer', label: 'Customer' },
  { value: 'created_at', label: 'Created date/time' },
  { value: 'dispatched_at', label: 'Dispatched date/time' },
  { value: 'started_at', label: 'Started date/time' },
  { value: 'completed_at', label: 'Completed date/time' },
  { value: 'job_status', label: 'Job status' },
];

const signatureSignerOptions: readonly {
  value: TemplateSignatureSignerType;
  label: string;
}[] = [
  { value: 'engineer', label: 'Engineer' },
  { value: 'site_contact', label: 'Site contact' },
  { value: 'customer', label: 'Customer' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'custom', label: 'Custom label' },
];

function fieldTypeHelp(type: FormFieldType): string {
  switch (type) {
    case 'address':
      return 'Structured postal address with separate address lines, town/city, county and postcode.';
    case 'file_upload':
      return 'Adds a document/file attachment field. Actual upload handling is enabled in the media phase.';
    case 'single_photograph':
      return 'Adds one photograph to the form. Camera and gallery capture are enabled in the media phase.';
    case 'multiple_photographs':
      return 'Adds a configurable collection of photographs. Capture is enabled in the media phase.';
    case 'signature':
      return 'Adds a drawn signature field and records who is expected to sign.';
    case 'gps':
      return 'Captures the device current position when the field is completed.';
    case 'map_location':
      return 'Lets the engineer choose a position manually on a map.';
    case 'system_field':
      return 'Filled automatically from job or system information rather than typed by the engineer.';
    default:
      return `${getFieldTypeDefinition(type).label} field.`;
  }
}

function ruleExpression(rule: TemplateRule): TemplateConditionExpression {
  if ('when' in rule) {
    return rule.when.kind === 'group'
      ? rule.when
      : { kind: 'group', mode: 'all', conditions: [rule.when] };
  }
  return {
    kind: 'group',
    mode: rule.conditionMode,
    conditions: rule.conditions.map((condition) => ({ kind: 'condition', ...condition })),
  };
}

function nestedRule(
  rule: TemplateRule,
  changes: Partial<{
    effect: TemplateRule['effect'];
    targetFieldId: string | undefined;
    targetSectionId: string | undefined;
    message: string | undefined;
    value: unknown;
    when: TemplateConditionExpression;
  }>,
): TemplateRule {
  const effect = changes.effect ?? rule.effect;
  const currentWhen = ruleExpression(rule);
  const when = changes.when ?? currentWhen;
  const currentTargetFieldId = rule.targetFieldId;
  const currentTargetSectionId = rule.targetSectionId;
  const currentMessage = rule.message;
  const currentValue = rule.value;

  const next: {
    id: string;
    effect: TemplateRule['effect'];
    when: TemplateConditionExpression;
    targetFieldId?: string;
    targetSectionId?: string;
    message?: string;
    value?: unknown;
  } = {
    id: rule.id,
    effect,
    when,
  };

  if (fieldTargetEffects.has(effect)) {
    const targetFieldId = changes.targetFieldId ?? currentTargetFieldId;
    if (targetFieldId) next.targetFieldId = targetFieldId;
  }
  if (effect === 'navigate_to_section') {
    const targetSectionId = changes.targetSectionId ?? currentTargetSectionId;
    if (targetSectionId) next.targetSectionId = targetSectionId;
  }
  if (effect === 'display_warning' || effect === 'prevent_submission') {
    const message = changes.message ?? currentMessage;
    next.message = message ?? '';
  }
  if (effect === 'set_value') {
    next.value = changes.value !== undefined ? changes.value : currentValue;
  }

  return next;
}

function selectedValues(event: ChangeEvent<HTMLSelectElement>): string[] {
  return [...event.target.selectedOptions].map((option) => option.value);
}

function candidateCalculationFields(
  definition: TemplateDefinition,
  targetFieldId: string,
): readonly TemplateField[] {
  return definition.fields.filter((field) => {
    if (field.id === targetFieldId) return false;
    return getFieldTypeDefinition(field.type).valueBearing;
  });
}

function normaliseCalculationSources(
  operator: TemplateCalculation['operator'],
  candidates: readonly TemplateField[],
  current: readonly string[],
): string[] {
  const allowedIds = new Set(candidates.map((field) => field.id));
  const retained = current.filter((fieldId) => allowedIds.has(fieldId));
  if (operator === 'pass_fail') {
    const toggle = candidates.find((field) => field.type === 'toggle');
    return toggle ? [toggle.id] : [];
  }
  if (operator === 'duration_minutes' || operator === 'percentage' || operator === 'difference') {
    const source =
      retained.length >= 2 ? retained : candidates.slice(0, 2).map((field) => field.id);
    return source.slice(0, 2);
  }
  return retained.length > 0 ? retained : candidates.slice(0, 1).map((field) => field.id);
}

function fieldParent(definition: TemplateDefinition, fieldId: string): TemplateField | undefined {
  return definition.fields.find((field) => {
    if (field.type === 'repeating_section')
      return field.repeating?.fieldIds.includes(fieldId) ?? false;
    if (field.type === 'table_grid') {
      return field.grid?.columns.some((column) => column.fieldIds.includes(fieldId)) ?? false;
    }
    return false;
  });
}

export function FormBuilderPanel({
  businessId,
  csrfToken,
  supportAccessId,
  template,
  versionId,
  onClose,
}: {
  businessId: string;
  csrfToken: string;
  supportAccessId?: string | undefined;
  template: TemplateSummary;
  versionId: string;
  onClose: () => void;
}) {
  const [version, setVersion] = useState<TemplateVersionRecord>();
  const [referenceLists, setReferenceLists] = useState<ReferenceListSummary[]>([]);
  const [history, setHistory] = useState<TemplateDefinition[]>([]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [savedDefinitionJson, setSavedDefinitionJson] = useState('');
  const [selectedPageId, setSelectedPageId] = useState('');
  const [selectedSectionId, setSelectedSectionId] = useState('');
  const [selectedFieldId, setSelectedFieldId] = useState<string>();
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [showPreview, setShowPreview] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [validation, setValidation] = useState<TemplateValidationReport>();
  const [newNestedType, setNewNestedType] = useState<FormFieldType>('single_line_text');
  const [paletteDragType, setPaletteDragType] = useState<FormFieldType>();
  const [paletteDropTarget, setPaletteDropTarget] = useState<string>();

  const definition = history[historyIndex];
  const dirty = definition ? JSON.stringify(definition) !== savedDefinitionJson : false;

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const [versionResult, referenceResult] = await Promise.all([
        apiRequest<{ version: TemplateVersionRecord }>(
          `/api/v1/businesses/${businessId}/template-versions/${versionId}`,
          { supportAccessId },
        ),
        apiRequest<{ referenceLists: ReferenceListSummary[] }>(
          `/api/v1/businesses/${businessId}/reference-lists`,
          { supportAccessId },
        ),
      ]);
      const loaded = versionResult.version;
      setVersion(loaded);
      setReferenceLists(referenceResult.referenceLists);
      setHistory([loaded.definition]);
      setHistoryIndex(0);
      setSavedDefinitionJson(JSON.stringify(loaded.definition));
      setValidation(loaded.validation_report);
      const firstPage = loaded.definition.pages[0];
      const firstSection = firstPage
        ? loaded.definition.sections.find((section) => section.id === firstPage.sectionIds[0])
        : undefined;
      setSelectedPageId(firstPage?.id ?? '');
      setSelectedSectionId(firstSection?.id ?? '');
      setSelectedFieldId(undefined);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }, [businessId, supportAccessId, versionId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dirty]);

  const commitDefinition = (next: TemplateDefinition) => {
    setHistory((current) => [...current.slice(0, historyIndex + 1), next]);
    setHistoryIndex((current) => current + 1);
    setNotice(undefined);
  };

  const updateDefinition = (mutate: (next: TemplateDefinition) => void) => {
    if (!definition) return;
    const next = cloneDefinition(definition);
    mutate(next);
    commitDefinition(next);
  };

  const page = definition?.pages.find((candidate) => candidate.id === selectedPageId);
  const section = definition?.sections.find((candidate) => candidate.id === selectedSectionId);
  const selectedField = definition?.fields.find((field) => field.id === selectedFieldId);
  const selectedRepeating =
    selectedField?.type === 'repeating_section' ? selectedField.repeating : undefined;
  const selectedGrid = selectedField?.type === 'table_grid' ? selectedField.grid : undefined;
  const selectedParent =
    definition && selectedFieldId ? fieldParent(definition, selectedFieldId) : undefined;

  const fieldById = useMemo(
    () => new Map(definition?.fields.map((field) => [field.id, field]) ?? []),
    [definition],
  );

  const paletteGroups = useMemo(() => {
    const categories = [...new Set(phase4FieldTypes.map((fieldType) => fieldType.category))];
    return categories.map(
      (category) =>
        [
          category,
          phase4FieldTypes.filter((fieldType) => fieldType.category === category),
        ] as const,
    );
  }, []);

  const publishedReferenceLists = referenceLists.filter(
    (referenceList) =>
      referenceList.status === 'active' && referenceList.current_published_version_id,
  );

  const undo = () => {
    setHistoryIndex((current) => Math.max(0, current - 1));
    setNotice(undefined);
  };
  const redo = () => {
    setHistoryIndex((current) => Math.min(history.length - 1, current + 1));
    setNotice(undefined);
  };

  const save = async () => {
    if (!definition) return;
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const result = await apiRequest<{ version: TemplateVersionRecord }>(
        `/api/v1/businesses/${businessId}/template-versions/${versionId}`,
        {
          method: 'PATCH',
          csrfToken,
          supportAccessId,
          body: { definition },
        },
      );
      setVersion(result.version);
      setValidation(result.version.validation_report);
      setSavedDefinitionJson(JSON.stringify(result.version.definition));
      setHistory([result.version.definition]);
      setHistoryIndex(0);
      setNotice('Draft saved.');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const validate = async () => {
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      if (dirty) {
        const saved = await apiRequest<{ version: TemplateVersionRecord }>(
          `/api/v1/businesses/${businessId}/template-versions/${versionId}`,
          {
            method: 'PATCH',
            csrfToken,
            supportAccessId,
            body: { definition },
          },
        );
        setVersion(saved.version);
        setSavedDefinitionJson(JSON.stringify(saved.version.definition));
        setHistory([saved.version.definition]);
        setHistoryIndex(0);
      }
      const result = await apiRequest<{ validation: TemplateValidationReport }>(
        `/api/v1/businesses/${businessId}/template-versions/${versionId}/validate`,
        { method: 'POST', csrfToken, supportAccessId },
      );
      setValidation(result.validation);
      setNotice(
        result.validation.valid
          ? 'Form structure validation passed.'
          : 'Validation completed with issues.',
      );
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const closeBuilder = () => {
    if (dirty && !window.confirm('Discard unsaved Form Builder changes?')) return;
    onClose();
  };

  if (loading) return <LoadingPanel message="Loading Form Builder…" />;
  if (!definition || !version) {
    return (
      <section className="panel">
        <h2>Form Builder</h2>
        {error ? (
          <Alert title="Form Builder could not open">{error}</Alert>
        ) : (
          <Alert>Draft unavailable.</Alert>
        )}
        <button type="button" className="secondary" onClick={onClose}>
          Back to forms
        </button>
      </section>
    );
  }

  const reorderPages = (activeId: string, overId: string) => {
    updateDefinition((next) => {
      const ids = reorderIds(
        next.pages.map((candidate) => candidate.id),
        activeId,
        overId,
      );
      const byId = new Map(next.pages.map((candidate) => [candidate.id, candidate]));
      next.pages = ids.flatMap((id) => {
        const candidate = byId.get(id);
        return candidate ? [candidate] : [];
      });
    });
  };

  const reorderSections = (activeId: string, overId: string) => {
    if (!page) return;
    updateDefinition((next) => {
      const targetPage = next.pages.find((candidate) => candidate.id === page.id);
      if (targetPage) targetPage.sectionIds = reorderIds(targetPage.sectionIds, activeId, overId);
    });
  };

  const reorderFields = (targetSectionId: string, activeId: string, overId: string) => {
    updateDefinition((next) => {
      const targetSection = next.sections.find((candidate) => candidate.id === targetSectionId);
      if (targetSection)
        targetSection.fieldIds = reorderIds(targetSection.fieldIds, activeId, overId);
    });
  };

  const addNewPage = () => {
    const result = addPage(definition);
    commitDefinition(result.definition);
    setSelectedPageId(result.pageId);
    setSelectedSectionId(result.sectionId);
    setSelectedFieldId(undefined);
  };

  const addNewSection = () => {
    const result = addSection(definition, selectedPageId);
    if (!result.sectionId) return;
    commitDefinition(result.definition);
    setSelectedSectionId(result.sectionId);
    setSelectedFieldId(undefined);
  };

  const addNewField = (type: FormFieldType) => {
    if (!selectedSectionId) return;
    const result = addField(definition, selectedSectionId, type);
    if (!result.fieldId) return;
    commitDefinition(result.definition);
    setSelectedFieldId(result.fieldId);
  };

  const addNewFieldAt = (type: FormFieldType, targetSectionId: string, insertionIndex?: number) => {
    const result = addField(
      definition,
      targetSectionId,
      type,
      () => crypto.randomUUID(),
      insertionIndex,
    );
    if (!result.fieldId) return;
    const targetSection = result.definition.sections.find(
      (candidate) => candidate.id === targetSectionId,
    );
    commitDefinition(result.definition);
    setSelectedSectionId(targetSectionId);
    setSelectedPageId(targetSection?.pageId ?? selectedPageId);
    setSelectedFieldId(result.fieldId);
    setPaletteDropTarget(undefined);
  };

  const paletteTypeFromDrop = (event: DragEvent<HTMLElement>): FormFieldType | undefined => {
    const transferred = event.dataTransfer.getData('application/x-dispatch-form-field-type');
    if (transferred && isFormFieldType(transferred)) return transferred;
    return paletteDragType;
  };

  const beginPaletteDrag = (event: DragEvent<HTMLButtonElement>, type: FormFieldType) => {
    if (!selectedSectionId) {
      event.preventDefault();
      return;
    }
    event.dataTransfer.effectAllowed = 'copy';
    event.dataTransfer.setData('application/x-dispatch-form-field-type', type);
    event.dataTransfer.setData('text/plain', type);
    setPaletteDragType(type);
    setPaletteDropTarget(undefined);
  };

  const endPaletteDrag = () => {
    setPaletteDragType(undefined);
    setPaletteDropTarget(undefined);
  };

  const allowPaletteDrop = (event: DragEvent<HTMLElement>, dropTarget: string) => {
    const hasPaletteType = event.dataTransfer.types.includes(
      'application/x-dispatch-form-field-type',
    );
    if (!paletteDragType && !hasPaletteType) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
    setPaletteDropTarget(dropTarget);
  };

  const dropPaletteField = (
    event: DragEvent<HTMLElement>,
    targetSectionId: string,
    insertionIndex?: number,
  ) => {
    const type = paletteTypeFromDrop(event);
    if (!type) return;
    event.preventDefault();
    event.stopPropagation();
    addNewFieldAt(type, targetSectionId, insertionIndex);
    setPaletteDragType(undefined);
  };

  const deleteSelectedField = () => {
    if (!selectedFieldId || selectedParent) return;
    const label = selectedField?.label ?? 'this field';
    if (!window.confirm(`Delete ${label}?`)) return;
    commitDefinition(removeField(definition, selectedFieldId));
    setSelectedFieldId(undefined);
  };

  const moveSelectedField = (direction: -1 | 1) => {
    if (!section || !selectedFieldId || selectedParent) return;
    updateDefinition((next) => {
      const target = next.sections.find((candidate) => candidate.id === section.id);
      if (target) target.fieldIds = moveId(target.fieldIds, selectedFieldId, direction);
    });
  };

  const updateSelectedField = (changes: Partial<TemplateField>) => {
    if (!selectedFieldId) return;
    updateDefinition((next) => {
      const index = next.fields.findIndex((field) => field.id === selectedFieldId);
      const current = next.fields[index];
      if (!current || index < 0) return;
      next.fields[index] = { ...current, ...changes };
    });
  };

  const binding = selectedField
    ? definition.referenceBindings.find((candidate) => candidate.fieldId === selectedField.id)
    : undefined;

  const setReferenceBinding = (referenceListId: string) => {
    if (!selectedField) return;
    updateDefinition((next) => {
      next.referenceBindings = next.referenceBindings.filter(
        (candidate) => candidate.fieldId !== selectedField.id,
      );
      const index = next.fields.findIndex((field) => field.id === selectedField.id);
      const field = next.fields[index];
      if (!field) return;
      if (referenceListId) {
        next.referenceBindings.push({ fieldId: selectedField.id, referenceListId });
        const withoutOptions = { ...field };
        delete withoutOptions.options;
        next.fields[index] = withoutOptions;
      } else if (
        ['dropdown', 'radio_buttons', 'checkboxes'].includes(field.type) &&
        (field.options?.length ?? 0) === 0
      ) {
        next.fields[index] = {
          ...field,
          options: [{ id: 'option_1', label: 'Option 1', value: 'option_1' }],
        };
      }
    });
  };

  const updateRule = (ruleId: string, nextRule: TemplateRule) => {
    updateDefinition((next) => {
      next.rules = next.rules.map((rule) => (rule.id === ruleId ? nextRule : rule));
    });
  };

  const addRule = () => {
    const rule = createRule(definition, 'hide');
    if (!rule) return;
    updateDefinition((next) => next.rules.push(rule));
  };

  const removeRule = (ruleId: string) => {
    updateDefinition((next) => {
      next.rules = next.rules.filter((rule) => rule.id !== ruleId);
    });
  };

  const selectedCalculation = selectedField
    ? calculationForField(definition, selectedField.id)
    : undefined;

  const ensureCalculation = () => {
    if (selectedField?.type !== 'calculated_field') return;
    const candidates = candidateCalculationFields(definition, selectedField.id);
    const calculation: TemplateCalculation = {
      id: nextSymbolicId(
        'calculation',
        definition.calculations.map((candidate) => candidate.id),
      ),
      targetFieldId: selectedField.id,
      operator: 'sum',
      sourceFieldIds: candidates.slice(0, 1).map((field) => field.id),
    };
    commitDefinition(upsertCalculation(definition, calculation));
  };

  return (
    <section className="form-builder" aria-labelledby="form-builder-heading">
      <header className="form-builder-toolbar">
        <div className="form-builder-title">
          <button type="button" className="secondary" onClick={closeBuilder}>
            Back to forms
          </button>
          <div>
            <p className="eyebrow">Form Builder</p>
            <h2 id="form-builder-heading">{template.name}</h2>
            <p>
              Draft {version.version_number ?? 'new'} · {dirty ? 'Unsaved changes' : 'Saved'}
            </p>
          </div>
        </div>
        <div className="actions form-builder-actions">
          <button
            type="button"
            className="secondary"
            onClick={undo}
            disabled={historyIndex === 0 || busy}
          >
            Undo
          </button>
          <button
            type="button"
            className="secondary"
            onClick={redo}
            disabled={historyIndex >= history.length - 1 || busy}
          >
            Redo
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => void validate()}
            disabled={busy}
          >
            Validate
          </button>
          <button type="button" onClick={() => void save()} disabled={busy || !dirty}>
            {busy ? 'Working…' : 'Save draft'}
          </button>
        </div>
      </header>

      {error ? <Alert title="Form Builder request failed">{error}</Alert> : null}
      {notice ? <Alert tone="success">{notice}</Alert> : null}
      {validation && !validation.valid ? (
        <Alert tone="warning" title="Draft has validation issues">
          <ul className="builder-validation-list">
            {validation.errors.slice(0, 8).map((issue) => (
              <li key={`${issue.code}-${issue.path ?? issue.message}`}>{issue.message}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <div className="form-builder-layout">
        <aside className="builder-palette" aria-labelledby="builder-palette-heading">
          <h3 id="builder-palette-heading">Fields</h3>
          <p className="help-text" id="builder-palette-help">
            Click a field to add it to the selected section, or drag it onto the form to place it
            exactly where you need it.
          </p>
          {paletteGroups.map(([category, fieldTypes]) => (
            <section key={category} className="builder-palette-group">
              <h4>{groupLabel(category)}</h4>
              <div className="builder-palette-buttons">
                {fieldTypes.map((fieldType) => (
                  <button
                    key={fieldType.id}
                    type="button"
                    className={
                      paletteDragType === fieldType.id
                        ? 'secondary builder-palette-button is-dragging'
                        : 'secondary builder-palette-button'
                    }
                    onClick={() => addNewField(fieldType.id)}
                    draggable={Boolean(selectedSectionId)}
                    onDragStart={(event) => beginPaletteDrag(event, fieldType.id)}
                    onDragEnd={endPaletteDrag}
                    aria-describedby="builder-palette-help"
                    title="Click to add to the selected section, or drag onto the form"
                    disabled={!selectedSectionId}
                  >
                    <span>{fieldType.label}</span>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </aside>

        <section className="builder-canvas" aria-label="Form layout canvas">
          <div className="builder-page-toolbar">
            <SortableList
              ids={definition.pages.map((candidate) => candidate.id)}
              onReorder={reorderPages}
              ariaLabel="Form pages"
              orientation="horizontal"
            >
              <div className="builder-page-tabs">
                {definition.pages.map((candidate, index) => (
                  <SortableItem
                    key={candidate.id}
                    id={candidate.id}
                    className="builder-page-tab-wrap"
                  >
                    {(dragHandle) => (
                      <div className="builder-page-tab-controls">
                        {dragHandle}
                        <button
                          type="button"
                          className={
                            candidate.id === selectedPageId
                              ? 'builder-page-tab is-selected'
                              : 'builder-page-tab'
                          }
                          aria-pressed={candidate.id === selectedPageId}
                          onClick={() => {
                            setSelectedPageId(candidate.id);
                            setSelectedSectionId(candidate.sectionIds[0] ?? '');
                            setSelectedFieldId(undefined);
                          }}
                        >
                          {index + 1}. {candidate.title}
                        </button>
                        <button
                          type="button"
                          className="plain builder-move-button"
                          aria-label={`Move ${candidate.title} left`}
                          disabled={index === 0}
                          onClick={() =>
                            updateDefinition((next) => {
                              const ids = moveId(
                                next.pages.map((pageItem) => pageItem.id),
                                candidate.id,
                                -1,
                              );
                              const byId = new Map(
                                next.pages.map((pageItem) => [pageItem.id, pageItem]),
                              );
                              next.pages = ids.flatMap((id) => {
                                const pageItem = byId.get(id);
                                return pageItem ? [pageItem] : [];
                              });
                            })
                          }
                        >
                          ←
                        </button>
                        <button
                          type="button"
                          className="plain builder-move-button"
                          aria-label={`Move ${candidate.title} right`}
                          disabled={index === definition.pages.length - 1}
                          onClick={() =>
                            updateDefinition((next) => {
                              const ids = moveId(
                                next.pages.map((pageItem) => pageItem.id),
                                candidate.id,
                                1,
                              );
                              const byId = new Map(
                                next.pages.map((pageItem) => [pageItem.id, pageItem]),
                              );
                              next.pages = ids.flatMap((id) => {
                                const pageItem = byId.get(id);
                                return pageItem ? [pageItem] : [];
                              });
                            })
                          }
                        >
                          →
                        </button>
                      </div>
                    )}
                  </SortableItem>
                ))}
              </div>
            </SortableList>
            <button type="button" className="secondary" onClick={addNewPage}>
              Add page
            </button>
          </div>

          {page ? (
            <div className="builder-page-canvas">
              <div className="builder-page-title-field">
                <div className="form-field">
                  <label htmlFor="builder-page-title">Page title</label>
                  <input
                    id="builder-page-title"
                    value={page.title}
                    onChange={(event) =>
                      updateDefinition((next) => {
                        const target = next.pages.find((candidate) => candidate.id === page.id);
                        if (target) target.title = event.target.value;
                      })
                    }
                  />
                </div>
                <button
                  type="button"
                  className="danger"
                  disabled={definition.pages.length <= 1}
                  onClick={() => {
                    if (!window.confirm(`Delete ${page.title} and all fields on it?`)) return;
                    const pageIndex = definition.pages.findIndex(
                      (candidate) => candidate.id === page.id,
                    );
                    const next = removePage(definition, page.id);
                    commitDefinition(next);
                    const nextPage =
                      next.pages[Math.max(0, Math.min(pageIndex, next.pages.length - 1))];
                    setSelectedPageId(nextPage?.id ?? '');
                    setSelectedSectionId(nextPage?.sectionIds[0] ?? '');
                    setSelectedFieldId(undefined);
                  }}
                >
                  Delete page
                </button>
              </div>

              <SortableList
                ids={page.sectionIds}
                onReorder={reorderSections}
                ariaLabel={`Sections on ${page.title}`}
              >
                <div className="builder-sections">
                  {page.sectionIds.map((sectionId, sectionIndex) => {
                    const candidateSection = definition.sections.find(
                      (candidate) => candidate.id === sectionId,
                    );
                    if (!candidateSection) return null;
                    return (
                      <SortableItem
                        key={candidateSection.id}
                        id={candidateSection.id}
                        className="builder-section-wrap"
                      >
                        {(sectionDragHandle) => (
                          <section
                            className={[
                              'builder-section',
                              candidateSection.id === selectedSectionId ? 'is-selected' : '',
                              paletteDropTarget === `${candidateSection.id}:end`
                                ? 'is-palette-drop-target'
                                : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                            onClick={() => {
                              setSelectedSectionId(candidateSection.id);
                              setSelectedFieldId(undefined);
                            }}
                            onDragOver={(event) =>
                              allowPaletteDrop(event, `${candidateSection.id}:end`)
                            }
                            onDrop={(event) =>
                              dropPaletteField(
                                event,
                                candidateSection.id,
                                candidateSection.fieldIds.length,
                              )
                            }
                          >
                            <header className="builder-section-header">
                              <div className="builder-section-heading">
                                {sectionDragHandle}
                                <div>
                                  <span className="builder-kicker">Section {sectionIndex + 1}</span>
                                  <strong>{candidateSection.title}</strong>
                                </div>
                              </div>
                              <div className="actions builder-order-actions">
                                <button
                                  type="button"
                                  className="plain"
                                  aria-label={`Move ${candidateSection.title} up`}
                                  disabled={sectionIndex === 0}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    updateDefinition((next) => {
                                      const targetPage = next.pages.find(
                                        (candidate) => candidate.id === page.id,
                                      );
                                      if (targetPage) {
                                        targetPage.sectionIds = moveId(
                                          targetPage.sectionIds,
                                          candidateSection.id,
                                          -1,
                                        );
                                      }
                                    });
                                  }}
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  className="plain"
                                  aria-label={`Move ${candidateSection.title} down`}
                                  disabled={sectionIndex === page.sectionIds.length - 1}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    updateDefinition((next) => {
                                      const targetPage = next.pages.find(
                                        (candidate) => candidate.id === page.id,
                                      );
                                      if (targetPage) {
                                        targetPage.sectionIds = moveId(
                                          targetPage.sectionIds,
                                          candidateSection.id,
                                          1,
                                        );
                                      }
                                    });
                                  }}
                                >
                                  ↓
                                </button>
                              </div>
                            </header>

                            <SortableList
                              ids={candidateSection.fieldIds}
                              onReorder={(activeId, overId) =>
                                reorderFields(candidateSection.id, activeId, overId)
                              }
                              ariaLabel={`Fields in ${candidateSection.title}`}
                            >
                              <div className="builder-field-list">
                                {candidateSection.fieldIds.map((fieldId, fieldIndex) => {
                                  const candidateField = fieldById.get(fieldId);
                                  if (!candidateField) return null;
                                  const type = getFieldTypeDefinition(candidateField.type);
                                  return (
                                    <SortableItem
                                      key={candidateField.id}
                                      id={candidateField.id}
                                      className="builder-field-wrap"
                                    >
                                      {(fieldDragHandle) => (
                                        <article
                                          className={[
                                            'builder-field-card',
                                            candidateField.id === selectedFieldId
                                              ? 'is-selected'
                                              : '',
                                            paletteDropTarget ===
                                            `${candidateSection.id}:${fieldIndex}`
                                              ? 'is-palette-drop-target'
                                              : '',
                                          ]
                                            .filter(Boolean)
                                            .join(' ')}
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            setSelectedSectionId(candidateSection.id);
                                            setSelectedFieldId(candidateField.id);
                                          }}
                                          onDragOver={(event) =>
                                            allowPaletteDrop(
                                              event,
                                              `${candidateSection.id}:${fieldIndex}`,
                                            )
                                          }
                                          onDrop={(event) =>
                                            dropPaletteField(event, candidateSection.id, fieldIndex)
                                          }
                                        >
                                          <div className="builder-field-summary">
                                            {fieldDragHandle}
                                            <div>
                                              <span className="builder-field-type">
                                                {type.label}
                                              </span>
                                              <strong>{candidateField.label}</strong>
                                              <span className="builder-field-meta">
                                                {groupLabel(candidateField.ownership)}
                                                {candidateField.required ? ' · Required' : ''}
                                              </span>
                                            </div>
                                          </div>
                                          <div className="actions builder-order-actions">
                                            <button
                                              type="button"
                                              className="plain"
                                              aria-label={`Move ${candidateField.label} up`}
                                              disabled={fieldIndex === 0}
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                updateDefinition((next) => {
                                                  const targetSection = next.sections.find(
                                                    (candidate) =>
                                                      candidate.id === candidateSection.id,
                                                  );
                                                  if (targetSection) {
                                                    targetSection.fieldIds = moveId(
                                                      targetSection.fieldIds,
                                                      candidateField.id,
                                                      -1,
                                                    );
                                                  }
                                                });
                                              }}
                                            >
                                              ↑
                                            </button>
                                            <button
                                              type="button"
                                              className="plain"
                                              aria-label={`Move ${candidateField.label} down`}
                                              disabled={
                                                fieldIndex === candidateSection.fieldIds.length - 1
                                              }
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                updateDefinition((next) => {
                                                  const targetSection = next.sections.find(
                                                    (candidate) =>
                                                      candidate.id === candidateSection.id,
                                                  );
                                                  if (targetSection) {
                                                    targetSection.fieldIds = moveId(
                                                      targetSection.fieldIds,
                                                      candidateField.id,
                                                      1,
                                                    );
                                                  }
                                                });
                                              }}
                                            >
                                              ↓
                                            </button>
                                          </div>
                                        </article>
                                      )}
                                    </SortableItem>
                                  );
                                })}
                              </div>
                            </SortableList>

                            {candidateSection.fieldIds.length === 0 ? (
                              <div
                                className={
                                  paletteDropTarget === `${candidateSection.id}:end`
                                    ? 'builder-empty-drop-zone is-palette-drop-target'
                                    : 'builder-empty-drop-zone'
                                }
                                onDragOver={(event) =>
                                  allowPaletteDrop(event, `${candidateSection.id}:end`)
                                }
                                onDrop={(event) => dropPaletteField(event, candidateSection.id, 0)}
                              >
                                {paletteDragType
                                  ? 'Drop field here'
                                  : 'Select a field from the palette, or drag one here.'}
                              </div>
                            ) : (
                              <div
                                className={
                                  paletteDropTarget === `${candidateSection.id}:end`
                                    ? 'builder-field-drop-end is-palette-drop-target'
                                    : 'builder-field-drop-end'
                                }
                                onDragOver={(event) =>
                                  allowPaletteDrop(event, `${candidateSection.id}:end`)
                                }
                                onDrop={(event) =>
                                  dropPaletteField(
                                    event,
                                    candidateSection.id,
                                    candidateSection.fieldIds.length,
                                  )
                                }
                                aria-hidden={!paletteDragType}
                              >
                                {paletteDragType ? 'Drop field at end of section' : ''}
                              </div>
                            )}
                          </section>
                        )}
                      </SortableItem>
                    );
                  })}
                </div>
              </SortableList>

              <button type="button" className="secondary" onClick={addNewSection}>
                Add section
              </button>
            </div>
          ) : (
            <Alert tone="warning">This draft has no selectable page.</Alert>
          )}
        </section>

        <aside className="builder-properties" aria-labelledby="builder-properties-heading">
          <h3 id="builder-properties-heading">Properties</h3>

          {section && !selectedField ? (
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor="builder-section-title">Section title</label>
                <input
                  id="builder-section-title"
                  value={section.title}
                  onChange={(event) =>
                    updateDefinition((next) => {
                      const target = next.sections.find((candidate) => candidate.id === section.id);
                      if (target) target.title = event.target.value;
                    })
                  }
                />
                <p className="help-text builder-property-help">
                  Heading shown above the fields in this section.
                </p>
              </div>
              <div className="actions">
                <button
                  type="button"
                  className="danger"
                  disabled={(page?.sectionIds.length ?? 0) <= 1}
                  onClick={() => {
                    if (!window.confirm(`Delete ${section.title} and all fields inside it?`))
                      return;
                    const next = removeSection(definition, section.id);
                    commitDefinition(next);
                    const nextPage = next.pages.find(
                      (candidate) => candidate.id === selectedPageId,
                    );
                    setSelectedSectionId(nextPage?.sectionIds[0] ?? '');
                  }}
                >
                  Delete section
                </button>
              </div>
              <p className="help-text">Select a field on the canvas to edit its properties.</p>
            </div>
          ) : null}

          {selectedField ? (
            <div className="form-grid builder-properties-form">
              {selectedParent ? (
                <Alert tone="info">
                  Nested field inside <strong>{selectedParent.label}</strong>.
                </Alert>
              ) : null}

              <p className="help-text builder-field-type-help">
                <strong>{getFieldTypeDefinition(selectedField.type).label}:</strong>{' '}
                {fieldTypeHelp(selectedField.type)}
              </p>

              <div className="form-field">
                <label htmlFor="builder-field-label">Label</label>
                <input
                  id="builder-field-label"
                  value={selectedField.label}
                  onChange={(event) => updateSelectedField({ label: event.target.value })}
                />
                <p className="help-text builder-property-help">
                  Name shown on the form and used to identify the field in later layouts.
                </p>
              </div>

              <div className="form-field">
                <label htmlFor="builder-field-ownership">Completed by</label>
                <select
                  id="builder-field-ownership"
                  value={selectedField.ownership}
                  onChange={(event) =>
                    updateSelectedField({ ownership: event.target.value as FieldOwnership })
                  }
                >
                  {getFieldTypeDefinition(selectedField.type).allowedOwnerships.map((ownership) => (
                    <option key={ownership} value={ownership}>
                      {groupLabel(ownership)}
                    </option>
                  ))}
                </select>
                <p className="help-text builder-property-help">
                  Controls who is expected to complete or supply this field.
                </p>
              </div>

              {!selectedParent ? (
                <div className="form-field">
                  <label htmlFor="builder-field-section">Section</label>
                  <select
                    id="builder-field-section"
                    value={
                      definition.sections.find((candidate) =>
                        candidate.fieldIds.includes(selectedField.id),
                      )?.id ?? ''
                    }
                    onChange={(event) => {
                      const targetSectionId = event.target.value;
                      const next = moveFieldToSection(
                        definition,
                        selectedField.id,
                        targetSectionId,
                      );
                      commitDefinition(next);
                      const targetSection = next.sections.find(
                        (candidate) => candidate.id === targetSectionId,
                      );
                      if (targetSection) {
                        setSelectedSectionId(targetSection.id);
                        setSelectedPageId(targetSection.pageId);
                      }
                    }}
                  >
                    {definition.pages.flatMap((candidatePage) =>
                      candidatePage.sectionIds.flatMap((candidateSectionId) => {
                        const candidateSection = definition.sections.find(
                          (candidate) => candidate.id === candidateSectionId,
                        );
                        return candidateSection
                          ? [
                              <option key={candidateSection.id} value={candidateSection.id}>
                                {candidatePage.title} — {candidateSection.title}
                              </option>,
                            ]
                          : [];
                      }),
                    )}
                  </select>
                  <p className="help-text builder-property-help">
                    Moves the field without changing its identity or any logic references.
                  </p>
                </div>
              ) : null}

              {getFieldTypeDefinition(selectedField.type).valueBearing ? (
                <div>
                  <label className="checkbox-field">
                    <input
                      type="checkbox"
                      checked={selectedField.required}
                      onChange={(event) => updateSelectedField({ required: event.target.checked })}
                    />
                    <span>Required</span>
                  </label>
                  <p className="help-text builder-property-help">
                    The field must contain a value while it is visible before the form can continue.
                  </p>
                </div>
              ) : null}

              <div className="form-field">
                <label htmlFor="builder-field-help">Help text</label>
                <textarea
                  id="builder-field-help"
                  value={selectedField.helpText ?? ''}
                  onChange={(event) =>
                    updateSelectedField({ helpText: event.target.value || undefined })
                  }
                />
                <p className="help-text builder-property-help">
                  Guidance shown beneath the field to the person completing the form.
                </p>
              </div>

              {getFieldTypeDefinition(selectedField.type).valueBearing &&
              ![
                'calculated_field',
                'system_field',
                'address',
                'file_upload',
                'single_photograph',
                'multiple_photographs',
                'signature',
                'gps',
                'map_location',
              ].includes(selectedField.type) ? (
                <>
                  <div className="form-field">
                    <label htmlFor="builder-field-placeholder">Placeholder</label>
                    <input
                      id="builder-field-placeholder"
                      value={selectedField.placeholder ?? ''}
                      onChange={(event) =>
                        updateSelectedField({ placeholder: event.target.value || undefined })
                      }
                    />
                    <p className="help-text builder-property-help">
                      Prompt or example shown inside an empty field; it is not saved as an answer.
                    </p>
                  </div>
                  <div className="form-field">
                    <label htmlFor="builder-field-default">Default value</label>
                    <input
                      id="builder-field-default"
                      value={editableValue(selectedField.defaultValue)}
                      onChange={(event) =>
                        updateSelectedField({ defaultValue: event.target.value || undefined })
                      }
                    />
                    <p className="help-text builder-property-help">
                      Starting value used when no answer has been entered yet.
                    </p>
                  </div>
                </>
              ) : null}

              {selectedField.type === 'system_field' ? (
                <fieldset>
                  <legend>Auto-populated value</legend>
                  <div className="form-field">
                    <label htmlFor="builder-system-source">Source</label>
                    <select
                      id="builder-system-source"
                      value={selectedField.systemSource ?? 'job_number'}
                      onChange={(event) =>
                        updateSelectedField({
                          systemSource: event.target.value as TemplateSystemFieldSource,
                        })
                      }
                    >
                      {systemFieldSourceOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <p className="help-text builder-property-help">
                      Chooses the job or system value inserted automatically when the form runs.
                    </p>
                  </div>
                </fieldset>
              ) : null}

              {selectedField.type === 'file_upload' ? (
                <fieldset>
                  <legend>File upload</legend>
                  <div>
                    <label className="checkbox-field">
                      <input
                        type="checkbox"
                        checked={selectedField.fileUpload?.multiple ?? false}
                        onChange={(event) =>
                          updateSelectedField({
                            fileUpload: event.target.checked
                              ? {
                                  multiple: true,
                                  maxFiles:
                                    selectedField.fileUpload?.multiple === true
                                      ? selectedField.fileUpload.maxFiles
                                      : 10,
                                }
                              : { multiple: false, maxFiles: 1 },
                          })
                        }
                      />
                      <span>Allow multiple files</span>
                    </label>
                    <p className="help-text builder-property-help">
                      Allows one document by default, or several attachments when enabled.
                    </p>
                  </div>
                  {selectedField.fileUpload?.multiple ? (
                    <div className="form-field">
                      <label htmlFor="builder-file-max">Maximum files</label>
                      <input
                        id="builder-file-max"
                        type="number"
                        min="2"
                        max="20"
                        value={selectedField.fileUpload.maxFiles}
                        onChange={(event) =>
                          updateSelectedField({
                            fileUpload: {
                              multiple: true,
                              maxFiles: Number(event.target.value),
                            },
                          })
                        }
                      />
                      <p className="help-text builder-property-help">
                        Limits how many documents can be attached to this field.
                      </p>
                    </div>
                  ) : null}
                </fieldset>
              ) : null}

              {selectedField.type === 'multiple_photographs' ? (
                <fieldset>
                  <legend>Photographs</legend>
                  <div className="form-grid form-grid--two">
                    <div className="form-field">
                      <label htmlFor="builder-photo-min">Minimum photos</label>
                      <input
                        id="builder-photo-min"
                        type="number"
                        min="0"
                        max="50"
                        value={selectedField.photographs?.minPhotos ?? 0}
                        onChange={(event) =>
                          updateSelectedField({
                            photographs: {
                              minPhotos: Number(event.target.value),
                              maxPhotos: selectedField.photographs?.maxPhotos ?? 10,
                            },
                          })
                        }
                      />
                      <p className="help-text builder-property-help">
                        Minimum number of photographs expected for this field.
                      </p>
                    </div>
                    <div className="form-field">
                      <label htmlFor="builder-photo-max">Maximum photos</label>
                      <input
                        id="builder-photo-max"
                        type="number"
                        min="1"
                        max="50"
                        value={selectedField.photographs?.maxPhotos ?? 10}
                        onChange={(event) =>
                          updateSelectedField({
                            photographs: {
                              minPhotos: selectedField.photographs?.minPhotos ?? 0,
                              maxPhotos: Number(event.target.value),
                            },
                          })
                        }
                      />
                      <p className="help-text builder-property-help">
                        Maximum number of photographs that may be attached.
                      </p>
                    </div>
                  </div>
                </fieldset>
              ) : null}

              {selectedField.type === 'signature' ? (
                <fieldset>
                  <legend>Signature</legend>
                  <div className="form-field">
                    <label htmlFor="builder-signature-signer">Signer</label>
                    <select
                      id="builder-signature-signer"
                      value={selectedField.signature?.signerType ?? 'site_contact'}
                      onChange={(event) => {
                        const signerType = event.target.value as TemplateSignatureSignerType;
                        updateSelectedField({
                          signature: {
                            signerType,
                            ...(signerType === 'custom' &&
                            selectedField.signature?.customSignerLabel
                              ? {
                                  customSignerLabel: selectedField.signature.customSignerLabel,
                                }
                              : {}),
                          },
                        });
                      }}
                    >
                      {signatureSignerOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <p className="help-text builder-property-help">
                      Identifies whose signature is expected when the field is completed.
                    </p>
                  </div>
                  {selectedField.signature?.signerType === 'custom' ? (
                    <div className="form-field">
                      <label htmlFor="builder-signature-custom">Custom signer label</label>
                      <input
                        id="builder-signature-custom"
                        value={selectedField.signature.customSignerLabel ?? ''}
                        onChange={(event) =>
                          updateSelectedField({
                            signature: {
                              signerType: 'custom',
                              ...(event.target.value
                                ? { customSignerLabel: event.target.value }
                                : {}),
                            },
                          })
                        }
                      />
                      <p className="help-text builder-property-help">
                        Wording shown for the signer, for example “Building manager”.
                      </p>
                    </div>
                  ) : null}
                </fieldset>
              ) : null}

              {['dropdown', 'radio_buttons', 'checkboxes'].includes(selectedField.type) ? (
                <fieldset>
                  <legend>Choices</legend>
                  <div className="form-field">
                    <label htmlFor="builder-reference-list">Source</label>
                    <select
                      id="builder-reference-list"
                      value={binding?.referenceListId ?? ''}
                      onChange={(event) => setReferenceBinding(event.target.value)}
                    >
                      <option value="">Inline choices</option>
                      {publishedReferenceLists.map((referenceList) => (
                        <option key={referenceList.id} value={referenceList.id}>
                          {referenceList.name}
                        </option>
                      ))}
                    </select>
                    <p className="help-text builder-property-help">
                      Use choices entered here or bind the field to a published managed reference
                      list.
                    </p>
                  </div>

                  {!binding ? (
                    <div className="builder-choice-options">
                      {(selectedField.options ?? []).map((option, optionIndex) => (
                        <div className="builder-choice-option" key={option.id}>
                          <input
                            aria-label={`Choice ${optionIndex + 1} label`}
                            value={option.label}
                            onChange={(event) =>
                              updateDefinition((next) => {
                                const field = next.fields.find(
                                  (candidate) => candidate.id === selectedField.id,
                                );
                                const target = field?.options?.find(
                                  (candidate) => candidate.id === option.id,
                                );
                                if (target) target.label = event.target.value;
                              })
                            }
                          />
                          <input
                            aria-label={`Choice ${optionIndex + 1} value`}
                            value={option.value}
                            onChange={(event) =>
                              updateDefinition((next) => {
                                const field = next.fields.find(
                                  (candidate) => candidate.id === selectedField.id,
                                );
                                const target = field?.options?.find(
                                  (candidate) => candidate.id === option.id,
                                );
                                if (target) target.value = event.target.value;
                              })
                            }
                          />
                          <button
                            type="button"
                            className="plain"
                            disabled={(selectedField.options?.length ?? 0) <= 1}
                            onClick={() =>
                              updateDefinition((next) => {
                                const field = next.fields.find(
                                  (candidate) => candidate.id === selectedField.id,
                                );
                                if (!field?.options || field.options.length <= 1) return;
                                field.options = field.options.filter(
                                  (candidate) => candidate.id !== option.id,
                                );
                              })
                            }
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="secondary"
                        onClick={() =>
                          updateDefinition((next) => {
                            const field = next.fields.find(
                              (candidate) => candidate.id === selectedField.id,
                            );
                            if (!field) return;
                            const currentOptions = field.options ?? [];
                            const id = nextSymbolicId(
                              'option',
                              currentOptions.map((option) => option.id),
                            );
                            field.options = [
                              ...currentOptions,
                              {
                                id,
                                label: `Option ${currentOptions.length + 1}`,
                                value: id,
                              },
                            ];
                          })
                        }
                      >
                        Add choice
                      </button>
                    </div>
                  ) : (
                    <p className="help-text">
                      Choices are supplied by the published managed reference list.
                    </p>
                  )}
                </fieldset>
              ) : null}

              {selectedField.type === 'repeating_section' && selectedRepeating ? (
                <fieldset>
                  <legend>Repeating section</legend>
                  <div className="form-grid form-grid--two">
                    <div className="form-field">
                      <label htmlFor="builder-repeat-min">Minimum entries</label>
                      <input
                        id="builder-repeat-min"
                        type="number"
                        min="0"
                        max="100"
                        value={selectedRepeating.minEntries}
                        onChange={(event) =>
                          updateSelectedField({
                            repeating: {
                              ...selectedRepeating,
                              minEntries: Number(event.target.value),
                            },
                          })
                        }
                      />
                      <p className="help-text builder-property-help">
                        Minimum number of repeated entries required.
                      </p>
                    </div>
                    <div className="form-field">
                      <label htmlFor="builder-repeat-max">Maximum entries</label>
                      <input
                        id="builder-repeat-max"
                        type="number"
                        min="1"
                        max="100"
                        value={selectedRepeating.maxEntries}
                        onChange={(event) =>
                          updateSelectedField({
                            repeating: {
                              ...selectedRepeating,
                              maxEntries: Number(event.target.value),
                            },
                          })
                        }
                      />
                      <p className="help-text builder-property-help">
                        Maximum number of repeated entries allowed.
                      </p>
                    </div>
                  </div>
                  <div className="form-field">
                    <label htmlFor="builder-repeat-label">Add another wording</label>
                    <input
                      id="builder-repeat-label"
                      value={selectedRepeating.addAnotherLabel ?? ''}
                      onChange={(event) =>
                        updateSelectedField({
                          repeating: {
                            ...selectedRepeating,
                            addAnotherLabel: event.target.value || undefined,
                          },
                        })
                      }
                    />
                    <p className="help-text builder-property-help">
                      Text shown on the button used to create another entry.
                    </p>
                  </div>
                  <div className="builder-nested-fields">
                    <strong>Fields in each entry</strong>
                    {selectedRepeating.fieldIds.map((childId) => {
                      const child = fieldById.get(childId);
                      return child ? (
                        <button
                          key={child.id}
                          type="button"
                          className="secondary"
                          onClick={() => setSelectedFieldId(child.id)}
                        >
                          {child.label}
                        </button>
                      ) : null;
                    })}
                  </div>
                  <div className="builder-add-nested">
                    <select
                      aria-label="New repeating field type"
                      value={newNestedType}
                      onChange={(event) => setNewNestedType(event.target.value as FormFieldType)}
                    >
                      {phase4FieldTypes
                        .filter(
                          (fieldType) => !['structure', 'layout'].includes(fieldType.category),
                        )
                        .map((fieldType) => (
                          <option key={fieldType.id} value={fieldType.id}>
                            {fieldType.label}
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => {
                        const result = addNestedField(definition, selectedField.id, newNestedType);
                        if (!result.fieldId) return;
                        commitDefinition(result.definition);
                        setSelectedFieldId(result.fieldId);
                      }}
                    >
                      Add entry field
                    </button>
                  </div>
                </fieldset>
              ) : null}

              {selectedField.type === 'table_grid' && selectedGrid ? (
                <fieldset>
                  <legend>Table/grid</legend>
                  <div className="builder-grid-columns">
                    {selectedGrid.columns.map((column, columnIndex) => (
                      <div className="builder-grid-column" key={column.id}>
                        <input
                          aria-label={`Column ${columnIndex + 1} label`}
                          value={column.label}
                          onChange={(event) =>
                            updateDefinition((next) => {
                              const field = next.fields.find(
                                (candidate) => candidate.id === selectedField.id,
                              );
                              const target = field?.grid?.columns.find(
                                (candidate) => candidate.id === column.id,
                              );
                              if (target) target.label = event.target.value;
                            })
                          }
                        />
                        {column.fieldIds.map((childId) => {
                          const child = fieldById.get(childId);
                          return child ? (
                            <button
                              type="button"
                              className="plain"
                              key={child.id}
                              onClick={() => setSelectedFieldId(child.id)}
                            >
                              Edit {child.label}
                            </button>
                          ) : null;
                        })}
                      </div>
                    ))}
                  </div>
                  <div className="builder-add-nested">
                    <select
                      aria-label="New grid field type"
                      value={newNestedType}
                      onChange={(event) => setNewNestedType(event.target.value as FormFieldType)}
                    >
                      {phase4FieldTypes
                        .filter(
                          (fieldType) => !['structure', 'layout'].includes(fieldType.category),
                        )
                        .map((fieldType) => (
                          <option key={fieldType.id} value={fieldType.id}>
                            {fieldType.label}
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => {
                        const result = addNestedField(definition, selectedField.id, newNestedType);
                        if (!result.fieldId) return;
                        commitDefinition(result.definition);
                        setSelectedFieldId(result.fieldId);
                      }}
                    >
                      Add column
                    </button>
                  </div>
                </fieldset>
              ) : null}

              {selectedField.type === 'calculated_field' ? (
                <fieldset>
                  <legend>Calculation</legend>
                  {selectedCalculation ? (
                    <>
                      <div className="form-field">
                        <label htmlFor="builder-calculation-operator">Operation</label>
                        <select
                          id="builder-calculation-operator"
                          value={selectedCalculation.operator}
                          onChange={(event) => {
                            const operator = event.target.value as TemplateCalculation['operator'];
                            const candidates = candidateCalculationFields(
                              definition,
                              selectedField.id,
                            );
                            commitDefinition(
                              upsertCalculation(definition, {
                                ...selectedCalculation,
                                operator,
                                sourceFieldIds: normaliseCalculationSources(
                                  operator,
                                  candidates,
                                  selectedCalculation.sourceFieldIds,
                                ),
                                ...(operator === 'percentage'
                                  ? { divideByZero: selectedCalculation.divideByZero ?? 'error' }
                                  : { divideByZero: undefined }),
                              }),
                            );
                          }}
                        >
                          <option value="sum">Sum</option>
                          <option value="difference">Difference</option>
                          <option value="product">Product</option>
                          <option value="duration_minutes">Duration in minutes</option>
                          <option value="percentage">Percentage</option>
                          <option value="pass_fail">Pass/fail</option>
                        </select>
                        <p className="help-text builder-property-help">
                          Determines how the selected source fields are combined.
                        </p>
                      </div>
                      <div className="form-field">
                        <label htmlFor="builder-calculation-sources">Source fields</label>
                        <select
                          id="builder-calculation-sources"
                          multiple
                          size={Math.min(
                            8,
                            Math.max(
                              3,
                              candidateCalculationFields(definition, selectedField.id).length,
                            ),
                          )}
                          value={[...selectedCalculation.sourceFieldIds]}
                          onChange={(event) =>
                            commitDefinition(
                              upsertCalculation(definition, {
                                ...selectedCalculation,
                                sourceFieldIds: selectedValues(event),
                              }),
                            )
                          }
                        >
                          {candidateCalculationFields(definition, selectedField.id).map(
                            (candidate) => (
                              <option key={candidate.id} value={candidate.id}>
                                {candidate.label}
                              </option>
                            ),
                          )}
                        </select>
                        <p className="help-text builder-property-help">
                          Fields whose values are used by this calculation.
                        </p>
                      </div>
                      {selectedCalculation.operator === 'percentage' ? (
                        <div className="form-field">
                          <label htmlFor="builder-divide-zero">Divide by zero</label>
                          <select
                            id="builder-divide-zero"
                            value={selectedCalculation.divideByZero ?? 'error'}
                            onChange={(event) =>
                              commitDefinition(
                                upsertCalculation(definition, {
                                  ...selectedCalculation,
                                  divideByZero: event.target.value === 'zero' ? 'zero' : 'error',
                                }),
                              )
                            }
                          >
                            <option value="error">Show validation error</option>
                            <option value="zero">Return 0</option>
                          </select>
                          <p className="help-text builder-property-help">
                            Chooses whether a zero divisor blocks validation or returns zero.
                          </p>
                        </div>
                      ) : null}
                      <button
                        type="button"
                        className="plain"
                        onClick={() =>
                          commitDefinition(removeCalculation(definition, selectedField.id))
                        }
                      >
                        Remove calculation
                      </button>
                    </>
                  ) : (
                    <button type="button" className="secondary" onClick={ensureCalculation}>
                      Configure calculation
                    </button>
                  )}
                </fieldset>
              ) : null}

              <div className="actions">
                {!selectedParent ? (
                  <>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => moveSelectedField(-1)}
                    >
                      Move up
                    </button>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => moveSelectedField(1)}
                    >
                      Move down
                    </button>
                    <button type="button" className="danger" onClick={deleteSelectedField}>
                      Delete field
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => setSelectedFieldId(selectedParent.id)}
                    >
                      Back to {selectedParent.label}
                    </button>
                    <button
                      type="button"
                      className="danger"
                      disabled={
                        selectedParent.type === 'repeating_section'
                          ? (selectedParent.repeating?.fieldIds.length ?? 0) <= 1
                          : (selectedParent.grid?.columns.length ?? 0) <= 1
                      }
                      onClick={() => {
                        if (!window.confirm(`Delete ${selectedField.label}?`)) return;
                        const next = removeField(definition, selectedField.id);
                        commitDefinition(next);
                        setSelectedFieldId(selectedParent.id);
                      }}
                    >
                      Delete nested field
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      <section className="builder-logic panel" aria-labelledby="builder-logic-heading">
        <div className="builder-subsection-heading">
          <div>
            <h3 id="builder-logic-heading">Conditional logic</h3>
            <p>Rules use nested AND/OR groups and the same Phase 4 rule model as the runtime.</p>
          </div>
          <button
            type="button"
            className="secondary"
            onClick={addRule}
            disabled={definition.fields.length === 0}
          >
            Add rule
          </button>
        </div>

        {definition.rules.length === 0 ? (
          <p className="help-text">No conditional rules configured.</p>
        ) : (
          <div className="builder-rules">
            {definition.rules.map((rule, index) => {
              const expression = ruleExpression(rule);
              return (
                <fieldset key={rule.id} className="builder-rule-card">
                  <legend>Rule {index + 1}</legend>
                  <div className="form-grid form-grid--two">
                    <div className="form-field">
                      <label htmlFor={`rule-effect-${rule.id}`}>Action</label>
                      <select
                        id={`rule-effect-${rule.id}`}
                        value={rule.effect}
                        onChange={(event) => {
                          const effect = event.target.value as TemplateRule['effect'];
                          const nextRule = nestedRule(rule, {
                            effect,
                            targetFieldId: definition.fields[0]?.id,
                            targetSectionId: definition.sections[0]?.id,
                            message:
                              effect === 'display_warning'
                                ? 'Warning'
                                : effect === 'prevent_submission'
                                  ? 'Cannot submit'
                                  : undefined,
                            value: effect === 'set_value' ? '' : undefined,
                          });
                          updateRule(rule.id, nextRule);
                        }}
                      >
                        <option value="show">Show field</option>
                        <option value="hide">Hide field</option>
                        <option value="require">Make field required</option>
                        <option value="unrequire">Make field optional</option>
                        <option value="set_value">Set field value</option>
                        <option value="display_warning">Display warning</option>
                        <option value="prevent_submission">Prevent submission</option>
                        <option value="navigate_to_section">Navigate to section</option>
                      </select>
                    </div>

                    {fieldTargetEffects.has(rule.effect) ? (
                      <div className="form-field">
                        <label htmlFor={`rule-target-${rule.id}`}>Target field</label>
                        <select
                          id={`rule-target-${rule.id}`}
                          value={rule.targetFieldId ?? ''}
                          onChange={(event) =>
                            updateRule(
                              rule.id,
                              nestedRule(rule, { targetFieldId: event.target.value }),
                            )
                          }
                        >
                          {definition.fields.map((field) => (
                            <option key={field.id} value={field.id}>
                              {field.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : null}

                    {rule.effect === 'navigate_to_section' ? (
                      <div className="form-field">
                        <label htmlFor={`rule-section-${rule.id}`}>Target section</label>
                        <select
                          id={`rule-section-${rule.id}`}
                          value={rule.targetSectionId ?? ''}
                          onChange={(event) =>
                            updateRule(
                              rule.id,
                              nestedRule(rule, { targetSectionId: event.target.value }),
                            )
                          }
                        >
                          {definition.sections.map((candidate) => (
                            <option key={candidate.id} value={candidate.id}>
                              {candidate.title}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : null}

                    {rule.effect === 'set_value' ? (
                      <div className="form-field">
                        <label htmlFor={`rule-value-${rule.id}`}>Value</label>
                        <input
                          id={`rule-value-${rule.id}`}
                          value={editableValue(rule.value)}
                          onChange={(event) =>
                            updateRule(rule.id, nestedRule(rule, { value: event.target.value }))
                          }
                        />
                      </div>
                    ) : null}

                    {rule.effect === 'display_warning' || rule.effect === 'prevent_submission' ? (
                      <div className="form-field">
                        <label htmlFor={`rule-message-${rule.id}`}>Message</label>
                        <input
                          id={`rule-message-${rule.id}`}
                          value={rule.message ?? ''}
                          onChange={(event) =>
                            updateRule(rule.id, nestedRule(rule, { message: event.target.value }))
                          }
                        />
                      </div>
                    ) : null}
                  </div>

                  <ConditionEditor
                    expression={expression}
                    fields={definition.fields}
                    onChange={(when) => updateRule(rule.id, nestedRule(rule, { when }))}
                  />

                  <button type="button" className="danger" onClick={() => removeRule(rule.id)}>
                    Delete rule
                  </button>
                </fieldset>
              );
            })}
          </div>
        )}
      </section>

      <section className="builder-preview-panel panel" aria-labelledby="builder-preview-heading">
        <div className="builder-subsection-heading">
          <div>
            <h3 id="builder-preview-heading">Live preview</h3>
            <p>
              Preview uses the shared Phase 4 runtime engine, including rules, calculations and page
              validation.
            </p>
          </div>
          <div className="actions">
            <button
              type="button"
              className="secondary"
              aria-pressed={previewMode === 'desktop'}
              onClick={() => setPreviewMode('desktop')}
            >
              Desktop
            </button>
            <button
              type="button"
              className="secondary"
              aria-pressed={previewMode === 'mobile'}
              onClick={() => setPreviewMode('mobile')}
            >
              Mobile
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() => setShowPreview((current) => !current)}
            >
              {showPreview ? 'Hide preview' : 'Show preview'}
            </button>
          </div>
        </div>
        {showPreview ? <FormRuntimePreview definition={definition} mode={previewMode} /> : null}
      </section>

      <section
        className="panel builder-phase-boundary"
        aria-labelledby="builder-phase-boundary-heading"
      >
        <h3 id="builder-phase-boundary-heading">Phase boundary</h3>
        <p>
          Form structure, logic and runtime preview are available in Phase 4. PDF layout and
          publication completeness checks remain in Phase 5; jobs, offline job sync, media capture
          and immutable submission remain in their later approved phases.
        </p>
      </section>
    </section>
  );
}
